import type { components } from '@awj/api-client';
import { currencyLabel, type Locale } from '@awj/i18n';

import type { useI18n } from '~/i18n/index.tsx';

import { auditActionLabel } from './audit-action.ts';
import { couponStanding } from './coupon-standing.ts';
import type { ExportColumn } from './export.ts';

type Schema = components['schemas'];
type T = ReturnType<typeof useI18n>['t'];

/**
 * What each table writes to Excel.
 *
 * The same columns as the table on screen, in the same order, with the stacked cells pulled apart
 * — a customer's name and email, a product's name and SKU — and a few facts the table only hints
 * at given a column of their own, such as a product's other-language name.
 */

/** Money columns carry the currency in the heading, so the cells stay plain numbers that sum. */
function inCurrency(header: string, locale: Locale): string {
  return `${header} (${currencyLabel(locale)})`;
}

/** Both names, the reader's own language first. */
function names<Row extends { nameEn: string; nameAr: string }>(t: T, locale: Locale): ExportColumn<Row>[] {
  const english: ExportColumn<Row> = { header: t('export.nameEn'), value: (row) => row.nameEn };
  const arabic: ExportColumn<Row> = { header: t('export.nameAr'), value: (row) => row.nameAr };

  return locale === 'ar' ? [arabic, english] : [english, arabic];
}

function orderStatus(t: T, status: string): string {
  return t(`orderStatus.${status}` as 'orderStatus.Paid');
}

/** A customer's own orders — the order list without the customer, who is the page's subject. */
export function customerOrderColumns(t: T, locale: Locale): ExportColumn<Schema['CustomerOrderResponse']>[] {
  return [
    { header: t('orders.number'), value: (order) => order.number },
    { header: t('orders.placed'), kind: 'dateTime', value: (order) => order.placedAt },
    { header: t('orders.status'), value: (order) => orderStatus(t, order.status) },
    { header: t('orders.items'), kind: 'number', value: (order) => order.itemCount },
    { header: inCurrency(t('orders.total'), locale), kind: 'money', value: (order) => order.grandTotal },
  ];
}

export function orderColumns(t: T, locale: Locale): ExportColumn<Schema['OrderListItemResponse']>[] {
  const [number, ...rest] = customerOrderColumns(t, locale);

  return [
    number!,
    { header: t('orders.customer'), value: (order) => order.customerName },
    { header: t('auth.email'), value: (order) => order.email },
    ...rest,
  ];
}

export function orderItemColumns(t: T, locale: Locale): ExportColumn<Schema['OrderItemResponse']>[] {
  return [
    { header: t('orders.item'), value: (item) => item.name },
    { header: t('catalog.variant'), value: (item) => item.variantLabel },
    { header: t('catalog.sku'), value: (item) => item.sku },
    { header: inCurrency(t('orders.unitPrice'), locale), kind: 'money', value: (item) => item.unitPrice },
    { header: t('orders.quantity'), kind: 'number', value: (item) => item.quantity },
    { header: inCurrency(t('orders.lineTotal'), locale), kind: 'money', value: (item) => item.lineTotal },
  ];
}

export function productColumns(t: T, locale: Locale): ExportColumn<Schema['AdminProductListItemResponse']>[] {
  return [
    { header: t('catalog.sku'), value: (product) => product.sku },
    ...names<Schema['AdminProductListItemResponse']>(t, locale),
    {
      header: t('catalog.category'),
      value: (product) => (locale === 'ar' ? product.categoryNameAr : product.categoryNameEn),
    },
    {
      header: t('catalog.status'),
      value: (product) => t(`productStatus.${product.status}` as 'productStatus.Active'),
    },
    {
      header: t('catalog.featured'),
      value: (product) => (product.isFeatured ? t('export.yes') : t('export.no')),
    },
    { header: t('catalog.stock'), kind: 'number', value: (product) => product.availableStock },
    {
      header: t('export.stockAlert'),
      // Sold out is not "low" to the API, and is the more urgent of the two.
      value: (product) =>
        product.availableStock === 0
          ? t('catalog.outOfStock')
          : product.isLowStock
            ? t('catalog.lowStock')
            : null,
    },
    { header: inCurrency(t('catalog.price'), locale), kind: 'money', value: (product) => product.price },
  ];
}

/** `all` is the whole tree, for naming each category's parent. */
export function categoryColumns(
  t: T,
  locale: Locale,
  all: Schema['AdminCategoryResponse'][],
): ExportColumn<Schema['AdminCategoryResponse']>[] {
  const nameOf = (category: Schema['AdminCategoryResponse']) =>
    locale === 'ar' ? category.nameAr : category.nameEn;

  return [
    ...names<Schema['AdminCategoryResponse']>(t, locale),
    {
      header: t('catalog.parentCategory'),
      value: (category) => {
        const parent = all.find((candidate) => candidate.id === category.parentId);

        return parent ? nameOf(parent) : null;
      },
    },
    { header: t('catalog.slug'), value: (category) => (locale === 'ar' ? category.slugAr : category.slugEn) },
    {
      header: t('catalog.status'),
      value: (category) => (category.isActive ? t('catalog.active') : t('catalog.hidden')),
    },
    { header: t('catalog.productCount'), kind: 'number', value: (category) => category.productCount },
  ];
}

export function customerColumns(t: T, locale: Locale): ExportColumn<Schema['CustomerListItemResponse']>[] {
  return [
    { header: t('catalog.name'), value: (customer) => customer.fullName },
    { header: t('auth.email'), value: (customer) => customer.email },
    { header: t('export.phone'), value: (customer) => customer.phoneNumber },
    {
      header: t('customers.account'),
      value: (customer) =>
        customer.isBlocked
          ? t('customers.blocked')
          : !customer.emailConfirmed
            ? t('customers.unconfirmed')
            : t('customers.active'),
    },
    { header: t('customers.registered'), kind: 'date', value: (customer) => customer.registeredAt },
    { header: t('customers.lastOrder'), kind: 'date', value: (customer) => customer.lastOrderAt },
    { header: t('customers.orders'), kind: 'number', value: (customer) => customer.orderCount },
    { header: inCurrency(t('customers.spent'), locale), kind: 'money', value: (customer) => customer.totalSpent },
  ];
}

/**
 * The discount in two columns, one per kind, so neither mixes percentages with dirhams. The
 * coupon form's own headings already name the currency.
 */
export function couponColumns(t: T, now: number): ExportColumn<Schema['CouponResponse']>[] {
  const percentage = (coupon: Schema['CouponResponse']) => coupon.type === 'Percentage';

  return [
    { header: t('coupons.code'), value: (coupon) => coupon.code },
    {
      header: t('coupons.type'),
      value: (coupon) => (percentage(coupon) ? t('coupons.typePercentage') : t('coupons.typeFixed')),
    },
    { header: t('coupons.percentOff'), kind: 'number', value: (coupon) => (percentage(coupon) ? coupon.value : null) },
    { header: t('coupons.amountOff'), kind: 'money', value: (coupon) => (percentage(coupon) ? null : coupon.value) },
    { header: t('coupons.minimum'), kind: 'money', value: (coupon) => coupon.minimumOrderAmount },
    { header: t('coupons.cap'), kind: 'money', value: (coupon) => coupon.maximumDiscountAmount },
    { header: t('export.starts'), kind: 'date', value: (coupon) => coupon.startsAt },
    { header: t('export.ends'), kind: 'date', value: (coupon) => coupon.expiresAt },
    { header: t('coupons.used'), kind: 'number', value: (coupon) => coupon.timesUsed },
    { header: t('coupons.usageLimit'), kind: 'number', value: (coupon) => coupon.usageLimit },
    { header: t('catalog.status'), value: (coupon) => t(couponStanding(coupon, now).label) },
  ];
}

export function staffColumns(t: T): ExportColumn<Schema['StaffMemberResponse']>[] {
  return [
    { header: t('catalog.name'), value: (member) => member.fullName },
    { header: t('auth.email'), value: (member) => member.email },
    { header: t('settings.role'), value: (member) => t(`role.${member.role}` as 'role.Admin') },
    {
      header: t('customers.account'),
      value: (member) => (member.isBlocked ? t('customers.blocked') : t('customers.active')),
    },
    { header: t('settings.added'), kind: 'date', value: (member) => member.createdAt },
    { header: t('settings.lastSignIn'), kind: 'dateTime', value: (member) => member.lastSignInAt },
  ];
}

export function auditColumns(t: T): ExportColumn<Schema['AuditEntryResponse']>[] {
  return [
    { header: t('settings.auditWhen'), kind: 'dateTime', value: (entry) => entry.occurredAt },
    { header: t('settings.auditAction'), value: (entry) => auditActionLabel(entry.action, t) },
    { header: t('export.details'), value: (entry) => entry.summary },
    { header: t('settings.auditWho'), value: (entry) => entry.actorEmail ?? t('orders.bySystem') },
    {
      header: t('settings.auditWhat'),
      value: (entry) => (entry.entityName ? `${entry.entityName}${entry.entityId ? ` #${entry.entityId}` : ''}` : null),
    },
    { header: t('settings.auditFrom'), value: (entry) => entry.ipAddress },
    // The code the "What happened" words stand for, which is what a search across months wants.
    { header: t('export.actionCode'), value: (entry) => entry.action },
  ];
}
